import type { RouteObject } from 'react-router-dom';
import { Outlet, createBrowserRouter, redirect } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import MetadataProvider from '@/MetadataProvider';
import PropertyProvider from '@/PropertyProvider';
import { AppSideEffectProvider } from '@/app/AppSideEffectProvider';
import CriticalErrorPage from '@/app/CriticalErrorPage';
import NotFoundPage from '@/app/NotFoundPage';
import RedirectHandler from '@/app/RedirectHandler';
import { UserPersistQueryClientProvider } from '@/app/UserPersistQueryClientProvider';
import { MetadataMapProvider } from '@/app/metadata/MetadataMapProvider';
import { AxiosUserAuthGuard } from '@/axios/hooks/useAxiosTokenEffect';
import { GlobalEventAlertDialog } from '@/components/GlobalEventAlertDialog';
import { GlobalModalDialog } from '@/components/GlobalEventModalDialog';
import { GlobalTutorialModal } from '@/components/GlobalTutorialModal';
import { WebViewAuthProvider } from '@/features/web-view/web-view-auth-provider';
import { WebViewLoadProvider } from '@/features/web-view/web-view-load-provider';
import { GlobalLayout } from '@/layout/GlobalLayout';
import { UserAuthAtomProvider } from '@/state/cookieContext';

import { guestRoutes } from './auth';
import UserAuthGuard from './auth/AuthGuard';
import cafe24MasterRoutes from './cafe24-master-account/Cafe24MasterRoutes';
import { basicRoles } from './consts';
import { handlerRoute } from './handler-route';
import { managerRoutes } from './manager/ManagerRoutes';
import { shopAuthRoute } from './shop-route';

const userAuthRouter: RouteObject = {
  element: (
    <WebViewLoadProvider>
      <WebViewAuthProvider>
        <UserAuthGuard roles={basicRoles}>
          <AxiosUserAuthGuard>
            <UserPersistQueryClientProvider>
              <MetadataMapProvider>
                <Outlet />

                <MetadataProvider />
                <PropertyProvider />

                <ReactQueryDevtools initialIsOpen={false} />
              </MetadataMapProvider>
            </UserPersistQueryClientProvider>
          </AxiosUserAuthGuard>
        </UserAuthGuard>
      </WebViewAuthProvider>
    </WebViewLoadProvider>
  ),
  children: [shopAuthRoute, managerRoutes],
};

/**
 * 모든 라우트들의 최상위 레이아웃.
 */
export const AppRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter)([
  {
    element: (
      <Sentry.ErrorBoundary fallback={CriticalErrorPage}>
        <UserAuthAtomProvider>
          <GlobalLayout>
            <Outlet />

            <GlobalEventAlertDialog />
            <GlobalModalDialog />
            <GlobalTutorialModal />

            <AppSideEffectProvider />
          </GlobalLayout>
        </UserAuthAtomProvider>
      </Sentry.ErrorBoundary>
    ),
    children: [
      {
        path: '/',
        element: <RedirectHandler />,
      },
      guestRoutes,
      userAuthRouter,
      cafe24MasterRoutes,
      handlerRoute,

      // else
      {
        path: '/*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
